import { useRef } from "react";
import { Copy, Download, FileCode2 } from "lucide-react";
import { QRCodeCanvas, QRCodeSVG } from "qrcode.react";
import type { ShortLink } from "../../shared/types";
import { date, number } from "../lib/format";
import { Button, Dialog, useToast } from "./ui";

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function QrDialog({ link, onClose }: { link: ShortLink | null; onClose: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const toast = useToast();

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.shortUrl);
      toast.push("短链已复制");
    } catch (error) {
      toast.fromError(error);
    }
  };

  const downloadPng = () => {
    if (!link) return;
    const data = canvasRef.current?.toDataURL("image/png");
    if (!data) return;
    const anchor = document.createElement("a");
    anchor.href = data;
    anchor.download = `g2al-${link.slug}.png`;
    anchor.click();
  };

  const downloadSvg = () => {
    if (!link || !svgRef.current) return;
    const svg = new XMLSerializer().serializeToString(svgRef.current);
    downloadBlob(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }), `g2al-${link.slug}.svg`);
  };

  return (
    <Dialog open={Boolean(link)} onClose={onClose} title={link ? `/${link.slug}` : "二维码"} eyebrow="QR · EXPORT" wide>
      {link ? (
        <div className="qr-layout">
          <div className="qr-poster">
            <div className="qr-poster__mark">g2.al</div>
            <div className="qr-poster__code">
              <QRCodeSVG
                ref={svgRef}
                value={link.shortUrl}
                size={238}
                level="M"
                marginSize={0}
                bgColor="#f3f2f2"
                fgColor="#201e1d"
              />
              <QRCodeCanvas ref={canvasRef} value={link.shortUrl} size={1024} level="M" marginSize={4} className="visually-hidden" />
            </div>
            <strong>{link.shortUrl.replace(/^https?:\/\//, "")}</strong>
            <span>SCAN · FOLLOW · {link.expiresAt ? `EXPIRES ${date(link.expiresAt)}` : "NO EXPIRY"}</span>
          </div>
          <div className="qr-details">
            <span className="eyebrow">LINK OBJECT · {link.status.toUpperCase()}</span>
            <h3>为屏幕与印刷准备的链接物</h3>
            <dl className="detail-list">
              <div><dt>目标</dt><dd>{link.targetUrl || "已释放"}</dd></div>
              <div><dt>点击</dt><dd>{number(link.clickCount)}</dd></div>
              <div><dt>创建</dt><dd>{date(link.createdAt)}</dd></div>
              <div><dt>纠错</dt><dd>M · 中等</dd></div>
            </dl>
            <div className="stack-actions">
              <Button variant="primary" onClick={downloadPng}><Download size={15} />下载 PNG</Button>
              <Button onClick={downloadSvg}><FileCode2 size={15} />下载 SVG</Button>
              <Button variant="ghost" onClick={() => void copy()}><Copy size={15} />复制短链</Button>
            </div>
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}

